import { supabase } from '../hooks/useAuth';
import type { ApiResponse, Persona, VideoProject, TrackContext } from '../types';

const API_BASE = import.meta.env.VITE_API_URL || '/api/v1';

async function getAuthToken(): Promise<string | null> {
  const {
    data: { session },
  } = await supabase.auth.getSession();
  return session?.access_token || null;
}

async function fetchApi<T>(endpoint: string, options: RequestInit = {}): Promise<ApiResponse<T>> {
  try {
    const token = await getAuthToken();

    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      ...(options.headers as Record<string, string> || {}),
    };

    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    console.log(`[API] ${options.method || 'GET'} ${API_BASE}${endpoint}`);

    const response = await fetch(`${API_BASE}${endpoint}`, {
      ...options,
      headers,
    });

    let data;
    try {
      data = await response.json();
    } catch {
      data = { error: `HTTP ${response.status}: ${response.statusText}` };
    }

    if (!response.ok) {
      const errorMsg = data.error || data.message || data.details || `HTTP ${response.status}: ${response.statusText}`;
      console.error(`[API] Error:`, errorMsg, data);
      return { error: errorMsg };
    }

    return { data };
  } catch (error) {
    const errorMsg = error instanceof Error ? error.message : 'Network error';
    console.error('[API] Network error:', errorMsg, error);
    return { error: `Network error: ${errorMsg}` };
  }
}

export const api = {
  // Personas
  getPersonas: () => fetchApi<{ personas: Persona[] }>('/personas'),

  getPersona: (id: string) => fetchApi<{ persona: Persona }>(`/personas/${id}`),

  createPersona: (data: {
    artist_name: string;
    stage_name?: string;
    creation_method?: string;
    initial_vision?: string;
  }) =>
    fetchApi<{ persona: Persona }>('/personas', {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  updatePersona: (id: string, data: { artist_name?: string; stage_name?: string }) =>
    fetchApi<{ persona: Persona }>(`/personas/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    }),

  deletePersona: (id: string) =>
    fetchApi<{ success: boolean }>(`/personas/${id}`, {
      method: 'DELETE',
    }),

  // Persona Memory
  getPersonaMemory: (personaId: string) => fetchApi<{ memory: unknown }>(`/personas/${personaId}/memory`),

  // Tracks
  getPersonaTracks: (personaId: string) => fetchApi<{ tracks: TrackContext[] }>(`/personas/${personaId}/tracks`),

  getTrack: (trackId: string) => fetchApi<TrackContext>(`/tracks/${trackId}`),

  // Fast track insights (<200ms) - uses backbone's insights store
  getTrackInsights: async (trackId: string) => {
    const backboneUrl = import.meta.env.VITE_BACKBONE_URL || 'http://localhost:8080';
    try {
      const response = await fetch(`${backboneUrl}/api/track/${trackId}/insights`);
      const data = await response.json();
      return { data };
    } catch (error) {
      return { error: error instanceof Error ? error.message : 'Failed to fetch insights' };
    }
  },

  // Projects
  getProjects: (personaId?: string) => {
    const params = personaId ? `?persona_id=${personaId}` : '';
    return fetchApi<{ projects: VideoProject[] }>(`/projects${params}`);
  },

  getProject: (id: string) => fetchApi<{ project: VideoProject }>(`/projects/${id}`),

  createProject: (data: {
    persona_id: string;
    track_context_id: string;
    target_platform?: string;
    target_duration?: number;
    quality_tier?: string;
  }) =>
    fetchApi<{ project: VideoProject }>('/projects', {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  startGeneration: (projectId: string) =>
    fetchApi<{ success: boolean }>(`/projects/${projectId}/generate`, {
      method: 'POST',
    }),

  getProjectStatus: (projectId: string) =>
    fetchApi<{
      status: string;
      progress: number;
      output_url?: string;
    }>(`/projects/${projectId}/status`),

  cancelProject: (projectId: string) =>
    fetchApi<{ success: boolean }>(`/projects/${projectId}/cancel`, {
      method: 'POST',
    }),

  deleteProject: (id: string) =>
    fetchApi<{ success: boolean }>(`/projects/${id}`, {
      method: 'DELETE',
    }),

  // Uploads
  getPresignedUrl: (data: { filename: string; content_type: string; persona_id: string; upload_type: string }) =>
    fetchApi<{
      key: string;
      upload_url: string;
      max_size: number;
    }>('/upload/presign', {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  completeUpload: (data: { key: string; persona_id: string; upload_type: string; metadata?: Record<string, unknown> }) =>
    fetchApi<{
      success: boolean;
      record: unknown;
    }>('/upload/complete', {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  // Track status polling
  getTrackStatus: (trackContextId: string) =>
    fetchApi<{
      status: 'pending' | 'processing' | 'complete' | 'failed';
      stage?: 'chunking' | 'parallel_processing' | 'synthesizing' | null;
      error?: string;
      analyzed_at?: string;
      analysis?: {
        filename: string;
        genre: string | null;
        mood: string | null;
        tempo: number | null;
        duration: number | null;
        lyrics: string | null;
        summary: Record<string, unknown> | null;
        visual_style: Record<string, unknown> | null;
      };
    }>(`/upload/status/track/${trackContextId}`),

  // Update track title
  updateTrackTitle: (trackContextId: string, title: string) =>
    fetchApi<{ success: boolean }>(`/upload/tracks/${trackContextId}/title`, {
      method: 'PATCH',
      body: JSON.stringify({ title }),
    }),

  // Update track cultural context
  updateTrackCulture: (trackContextId: string, cultureCode: string | null) =>
    fetchApi<{ success: boolean }>(`/upload/tracks/${trackContextId}/culture`, {
      method: 'PATCH',
      body: JSON.stringify({ culture_code: cultureCode }),
    }),

  // Delete track
  deleteTrack: (trackContextId: string) =>
    fetchApi<{ success: boolean }>(`/upload/tracks/${trackContextId}`, {
      method: 'DELETE',
    }),
};
